import {
    useMutation,
    UseMutationResult,
    useQueryClient,
} from '@tanstack/react-query';
import { useAuthenticatedQuery } from 'flow/hooks/api/useAuthenticatedQuery';
import TicketsResource from 'flow/api/omnitickets';
import { QueueKey } from 'flow/types/EntityKeys';
import {
    AutoReplyTemplateDTO,
    AutoReplyTemplateKey,
    CreateAutoReplyTemplateDTO,
    UpdateAutoReplyTemplateDTO,
} from 'flow/types/AutoReplyTemplate';

const queryKey = (queueEntityKey: QueueKey) => [
    'autoReplyTemplates',
    queueEntityKey,
];

export const useAutoReplyTemplates = (queueEntityKey: QueueKey) =>
    useAuthenticatedQuery({
        queryKey: queryKey(queueEntityKey),
        queryFn: () =>
            new Promise<AutoReplyTemplateDTO[]>((resolve, reject) => {
                TicketsResource.getAutoReplyTemplates(
                    {
                        onResult: resolve,
                        onError: reject,
                        onClientError: reject,
                    },
                    queueEntityKey,
                );
            }),
    });

export const useCreateAutoReplyTemplate = (
    queueEntityKey: QueueKey,
): UseMutationResult<
    AutoReplyTemplateDTO,
    Error,
    CreateAutoReplyTemplateDTO
> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (dto: CreateAutoReplyTemplateDTO) =>
            new Promise<AutoReplyTemplateDTO>((resolve, reject) => {
                TicketsResource.createAutoReplyTemplate(
                    {
                        onResult: resolve,
                        onError: reject,
                        onClientError: reject,
                    },
                    dto,
                );
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKey(queueEntityKey),
            });
        },
    });
};

export const useUpdateAutoReplyTemplate = (
    queueEntityKey: QueueKey,
): UseMutationResult<
    AutoReplyTemplateDTO,
    Error,
    { key: AutoReplyTemplateKey; dto: UpdateAutoReplyTemplateDTO }
> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ key, dto }) =>
            new Promise<AutoReplyTemplateDTO>((resolve, reject) => {
                TicketsResource.updateAutoReplyTemplate(
                    {
                        onResult: resolve,
                        onError: reject,
                        onClientError: reject,
                    },
                    key,
                    dto,
                );
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKey(queueEntityKey),
            });
        },
    });
};

export const useDeleteAutoReplyTemplate = (
    queueEntityKey: QueueKey,
): UseMutationResult<void, Error, AutoReplyTemplateKey> => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (key: AutoReplyTemplateKey) =>
            new Promise<void>((resolve, reject) => {
                TicketsResource.deleteAutoReplyTemplate(
                    {
                        onResult: resolve,
                        onError: reject,
                        onClientError: reject,
                    },
                    key,
                );
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: queryKey(queueEntityKey),
            });
        },
    });
};
